import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { TONE_PILL, TONE_TEXT, type Tone } from '../../lib/tone';
import { statusInfo, type StatusKey } from '../../status';
import { Icon, type LucideIcon } from './icon';

export interface StatusPillProps {
  tone?: Tone;
  /** Ein bis drei Wörter. */
  children: ReactNode;
  /** `plain` ohne Fläche, für Tabellen mit vielen Zeilen. */
  variant?: 'pill' | 'plain';
  /**
   * `false`: ohne Punkt, für Kategorien wie die Themen eines Förderprogramms.
   * Eine Kategorie ist kein Status; der Punkt bleibt Zeichen für „Zustand“.
   */
  punkt?: boolean;
  /**
   * Icon statt Punkt, wenn ein Zustand mehrere Fälle in einem Ton hat und
   * das Icon sie unterscheidet (Quellzustand im Radar: gesperrt, umgezogen …).
   * Das Wort steht trotzdem immer da.
   */
  icon?: LucideIcon;
  className?: string;
}

/** Punkt plus Wort; die Farbe trägt nie allein die Bedeutung. */
export function StatusPill({ tone = 'neutral', children, variant = 'pill', punkt = true, icon, className }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap font-medium',
        variant === 'plain'
          ? cn('text-[13px] leading-[18px]', TONE_TEXT[tone])
          : cn('h-[22px] rounded-full text-xs', icon ? 'pl-1.5 pr-[9px]' : punkt ? 'pl-2 pr-[9px]' : 'px-[9px]', TONE_PILL[tone]),
        className,
      )}
    >
      {icon ? (
        <Icon icon={icon} size={13} className="shrink-0" />
      ) : (
        punkt && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      )}
      {children}
    </span>
  );
}

export interface StatusProps {
  /** Schlüssel aus STATUS, z. B. `quote_to_send` oder `angebot_raus`. */
  status: StatusKey | (string & NonNullable<unknown>);
  variant?: 'pill' | 'plain';
  /** Icon statt Punkt (siehe `StatusPill icon`). */
  icon?: LucideIcon;
  className?: string;
}

/** StatusPill für einen Status aus STATUS; Text und Ton kommen von dort. */
export function Status({ status, variant, icon, className }: StatusProps) {
  const s = statusInfo(status);
  return (
    <StatusPill tone={s.tone} variant={variant} icon={icon} className={className}>
      {s.label}
    </StatusPill>
  );
}
